import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import RolePicker from "../components/dashboard/RolePicker";
import SellerListings from "../components/dashboard/SellerListings";
import { SellerBookings, BuyerBookings } from "../components/dashboard/BookingLists";
import SavedListings from "../components/dashboard/SavedListings";
import OrganisationTab from "../components/dashboard/OrganisationTab";
import OrgInvitesInbox from "../components/dashboard/OrgInvitesInbox";

export default function Dashboard() {
  const { user, setUser, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState(null);
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [buyerBookings, setBuyerBookings] = useState([]);
  const [sellerBookings, setSellerBookings] = useState([]);

  const adminEligible = !!user?.admin_eligible;

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "seller" || user.role === "admin") {
      setTab("my-listings");
      api.get("/listings/mine").then((r) => setListings(r.data)).catch(() => {});
      api.get("/bookings/seller").then((r) => setSellerBookings(r.data)).catch(() => {});
    } else if (user.role === "buyer") {
      setTab("my-bookings");
      api.get("/bookings/buyer").then((r) => setBuyerBookings(r.data)).catch(() => {});
    }
    // Honour ?tab=org deep-link from notification clicks
    const wantTab = searchParams.get("tab");
    if (wantTab) setTab(wantTab);
  }, [user, searchParams]);

  if (loading || !user) {
    return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;
  }

  if (!user.role) {
    return <Layout><RolePicker user={user} setUser={setUser} adminEligible={adminEligible} /></Layout>;
  }

  const setRole = async (newRole) => {
    if (newRole === user.role) return;
    try {
      const { data } = await api.post("/auth/role", { role: newRole });
      setUser(data);
      toast.success(`${t("dash.switched")} ${t(`role.${newRole}`)}`);
    } catch {
      toast.error(t("dash.failed"));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t("dash.confirmDelete"))) return;
    try {
      await api.delete(`/listings/${id}`);
      setListings((l) => l.filter((x) => x.id !== id));
      toast.success(t("dash.deleted"));
    } catch {
      toast.error(t("dash.failed"));
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      const updater = (bs) => bs.map((b) => (b.id === bookingId ? { ...b, status } : b));
      if (user.role === "seller" || user.role === "admin") setSellerBookings(updater);
      else setBuyerBookings(updater);
      toast.success(`${t("dash.markedAs")} ${t(`status.${status}`)}`);
    } catch {
      toast.error(t("dash.failed"));
    }
  };

  const roleLabel = t(`role.${user.role}`);
  const isSellerLike = user.role === "seller" || user.role === "admin";

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-tech">{t("dash.label")} {roleLabel} ]</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("dash.hello")} {user.name.split(" ")[0]}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {adminEligible ? (
              <div className="flex gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]" data-testid="role-switcher">
                {["admin", "seller", "buyer"].map((r) => (
                  <button
                    key={r}
                    onClick={() => setRole(r)}
                    className={`px-3 py-2 text-xs font-mono-tech uppercase tracking-[0.1em] ${user.role === r ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`}
                    data-testid={`role-switch-${r}`}
                  >
                    {t(`role.view.${r}`)}
                  </button>
                ))}
              </div>
            ) : (
              <button onClick={() => setRole(user.role === "seller" ? "buyer" : "seller")} className="btn-ghost" data-testid="toggle-role-btn">
                {t("dash.switchTo")} {user.role === "seller" ? t("role.buyer") : t("role.seller")}
              </button>
            )}
            {isSellerLike && (
              <Link to="/listings/new" className="btn-primary" data-testid="new-listing-btn"><Plus size={14} /> {t("dash.newListing")}</Link>
            )}
          </div>
        </div>

        <div className="mt-6">
          <OrgInvitesInbox />
        </div>

        <div className="mt-8 flex gap-px bg-[var(--ink-200)] border border-[var(--ink-200)] flex-wrap">
          {isSellerLike ? (
            <>
              <button onClick={() => setTab("my-listings")} className={`px-5 py-3 text-sm font-mono-tech uppercase tracking-[0.1em] ${tab === "my-listings" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid="tab-my-listings">{t("dash.myListings")} ({listings.length})</button>
              <button onClick={() => setTab("incoming")} className={`px-5 py-3 text-sm font-mono-tech uppercase tracking-[0.1em] ${tab === "incoming" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid="tab-incoming-bookings">{t("dash.incoming")} ({sellerBookings.length})</button>
            </>
          ) : (
            <button onClick={() => setTab("my-bookings")} className={`px-5 py-3 text-sm font-mono-tech uppercase tracking-[0.1em] ${tab === "my-bookings" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid="tab-my-bookings">{t("dash.myBookings")} ({buyerBookings.length})</button>
          )}
          <button onClick={() => setTab("saved")} className={`px-5 py-3 text-sm font-mono-tech uppercase tracking-[0.1em] ${tab === "saved" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid="tab-saved">{t("saved.tab")}</button>
          <button onClick={() => setTab("org")} className={`px-5 py-3 text-sm font-mono-tech uppercase tracking-[0.1em] ${tab === "org" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid="tab-org">{t("org.tab")}</button>
        </div>

        <div className="mt-px border border-[var(--ink-200)] bg-[var(--surface)]">
          <div className="p-6">
            {tab === "my-listings" && <SellerListings listings={listings} onDelete={handleDelete} />}
            {tab === "incoming" && <SellerBookings bookings={sellerBookings} onUpdateStatus={updateStatus} />}
            {tab === "my-bookings" && <BuyerBookings bookings={buyerBookings} onUpdateStatus={updateStatus} />}
            {tab === "saved" && <SavedListings />}
            {tab === "org" && <OrganisationTab />}
          </div>
        </div>
      </div>
    </Layout>
  );
}
